import {
  Text, Paper, Flex, Select, Table, Box, Group, Badge, Card,
  ActionIcon, Loader, Stack, Divider,
} from '@mantine/core';
import { IconSearch, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useState, useMemo, useEffect } from 'react';
import api from './api';
import { WEEKDAYS_FULL, getMonthName } from './dashboardData';

// ============================================================
// CONSTANTES
// ============================================================

const MESES = [
  { value: '1', label: 'ENERO' }, { value: '2', label: 'FEBRERO' },
  { value: '3', label: 'MARZO' }, { value: '4', label: 'ABRIL' },
  { value: '5', label: 'MAYO' }, { value: '6', label: 'JUNIO' },
  { value: '7', label: 'JULIO' }, { value: '8', label: 'AGOSTO' },
  { value: '9', label: 'SEPTIEMBRE' }, { value: '10', label: 'OCTUBRE' },
  { value: '11', label: 'NOVIEMBRE' }, { value: '12', label: 'DICIEMBRE' },
];

const getYearOptions = () => {
  const y = new Date().getFullYear();
  return Array.from({ length: 7 }, (_, i) => ({ value: String(y - 3 + i), label: String(y - 3 + i) }));
};

const semaforo = (val) => {
  const n = parseFloat(val || '0');
  if (n >= 70) return { bg: '#e8f5e9', c: '#1b5e20' };
  if (n >= 40) return { bg: '#fff8e1', c: '#e65100' };
  if (n > 0) return { bg: '#fce4ec', c: '#b71c1c' };
  return { bg: '#f5f5f5', c: '#999' };
};

// ============================================================
// COMPONENT
// ============================================================

export default function Comercial() {
  const now = new Date();
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [anio, setAnio] = useState(now.getFullYear());

  // Filtros
  const [rutaFiltro, setRutaFiltro] = useState(null);
  const [vueloFiltro, setVueloFiltro] = useState(null);

  // Opciones de filtros
  const [rutaOptions, setRutaOptions] = useState([]);
  const [vueloOptions, setVueloOptions] = useState([]);

  // Data
  const [vueloData, setVueloData] = useState(null); // detalle del vuelo seleccionado
  const [resumenVuelos, setResumenVuelos] = useState([]); // resumen general
  const [loading, setLoading] = useState(false);

  // ============================================================
  // CARGAR RUTAS
  // ============================================================
  useEffect(() => {
    api.get(`/planeacion/v2/rutas?mes=${mes}&anio=${anio}`)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
        setRutaOptions(data.map((r) => ({ value: typeof r === 'string' ? r : r.Ruta || r.ruta, label: typeof r === 'string' ? r : r.Ruta || r.ruta })));
      })
      .catch(() => setRutaOptions([]));
  }, [mes, anio]);

  // ============================================================
  // CARGAR VUELOS cuando cambia ruta
  // ============================================================
  useEffect(() => {
    if (!rutaFiltro) { setVueloOptions([]); return; }
    api.get(`/planeacion/v2/vuelos?ruta=${rutaFiltro}&mes=${mes}&anio=${anio}`)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
        setVueloOptions(data.map((v) => {
          const num = typeof v === 'number' || typeof v === 'string' ? v : v.Vuelo || v.vuelo;
          return { value: String(num), label: `VLO ${num}` };
        }));
      })
      .catch(() => setVueloOptions([]));
  }, [rutaFiltro, mes, anio]);

  // ============================================================
  // CARGAR DETALLE VUELO
  // ============================================================
  useEffect(() => {
    if (!rutaFiltro || !vueloFiltro) { setVueloData(null); return; }
    setLoading(true);
    api.get(`/planeacion/v2/vuelo?ruta=${rutaFiltro}&vuelo=${vueloFiltro}&mes=${mes}&anio=${anio}`)
      .then((res) => setVueloData(res.data))
      .catch(() => setVueloData(null))
      .finally(() => setLoading(false));
  }, [rutaFiltro, vueloFiltro, mes, anio]);

  // ============================================================
  // CARGAR RESUMEN GENERAL
  // ============================================================
  useEffect(() => {
    api.get(`/planeacion/v2/resumen-vuelos?mes=${mes}&anio=${anio}`)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
        setResumenVuelos(data);
      })
      .catch(() => setResumenVuelos([]));
  }, [mes, anio]);

  // Navegación
  const prevMonth = () => { const m = mes === 1 ? 12 : mes - 1; const a = mes === 1 ? anio - 1 : anio; setMes(m); setAnio(a); setRutaFiltro(null); setVueloFiltro(null); };
  const nextMonth = () => { const m = mes === 12 ? 1 : mes + 1; const a = mes === 12 ? anio + 1 : anio; setMes(m); setAnio(a); setRutaFiltro(null); setVueloFiltro(null); };

  // Datos del vuelo
  const vuelo = vueloData?.vuelo || null;
  const clientes = vueloData?.clientes || [];
  const resumen = vueloData?.resumen || null;

  // FOC calculado
  const focKg = useMemo(() => {
    if (!vuelo || !clientes.length) return {};
    const capKg = (vuelo.CapTons ?? 0) * 1000;
    const result = {};
    WEEKDAYS_FULL.forEach((dia) => {
      let sum = 0;
      clientes.forEach((c) => { if (c.dias?.[dia]) sum += Number(c.kg ?? 0); });
      result[dia] = capKg > 0 ? Math.round((sum / capKg) * 100) + '%' : '0%';
    });
    return result;
  }, [vuelo, clientes]);

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <>
      {/* HEADER */}
      <Flex justify="space-between" align="center" mb="md" wrap="wrap" gap="md">
        <Text fw={700} size="xl" c="#020C41">Consulta Comercial</Text>
        <Group gap="xs">
          <ActionIcon size="sm" variant="light" color="#030D75" onClick={prevMonth}><IconChevronLeft size={14} /></ActionIcon>
          <Select value={String(mes)} onChange={(v) => { if (v) { setMes(parseInt(v, 10)); setRutaFiltro(null); setVueloFiltro(null); } }} data={MESES} size="xs" allowDeselect={false} style={{ width: 135 }} />
          <Select value={String(anio)} onChange={(v) => { if (v) { setAnio(parseInt(v, 10)); setRutaFiltro(null); setVueloFiltro(null); } }} data={getYearOptions()} size="xs" allowDeselect={false} style={{ width: 90 }} />
          <ActionIcon size="sm" variant="light" color="#030D75" onClick={nextMonth}><IconChevronRight size={14} /></ActionIcon>
        </Group>
      </Flex>

      {/* FILTROS */}
      <Paper shadow="xs" p="md" mb="md" radius="md">
        <Flex gap="md" align="end" wrap="wrap">
          <Select label="Ruta" placeholder="Seleccionar ruta..." data={rutaOptions} value={rutaFiltro}
            onChange={(v) => { setRutaFiltro(v); setVueloFiltro(null); }} searchable clearable
            leftSection={<IconSearch size={16} />} style={{ flex: 1, minWidth: 180 }} />
          <Select label="Vuelo" placeholder="Seleccionar vuelo..." data={vueloOptions} value={vueloFiltro}
            onChange={setVueloFiltro} searchable clearable disabled={!rutaFiltro}
            leftSection={<IconSearch size={16} />} style={{ flex: 1, minWidth: 160 }} />
        </Flex>
      </Paper>

      {/* LOADING */}
      {loading && <Flex justify="center" py="xl"><Loader size="sm" color="#020C41" /></Flex>}

      {/* DETALLE DEL VUELO */}
      {!loading && vuelo && (
        <Paper shadow="xs" radius="md" p="md" mb="md">
          {/* Info vuelo */}
          <Flex justify="space-between" align="center" mb="sm">
            <Group gap="sm">
              <Text size="lg" fw={700} c="#020C41">{vuelo.ID || `${vuelo.Ruta}${vuelo.Vuelo}`}</Text>
              <Badge color="am-blue" variant="light">{vuelo.Ruta}</Badge>
              <Badge color="am-pink" variant="light">VLO {vuelo.Vuelo}</Badge>
            </Group>
            <Text size="sm" fw={600} c="#030D75">ETD {vuelo.ETD || '—'}</Text>
          </Flex>

          {/* Capacidades */}
          <Card withBorder radius="sm" p="xs" mb="sm" style={{ backgroundColor: '#F2F2F0' }}>
            <Group gap="xl">
              <Box><Text size="xs" c="dimmed">Cap (T)</Text><Text size="sm" fw={600}>{vuelo.CapTons}</Text></Box>
              <Box><Text size="xs" c="dimmed">Cap (m³)</Text><Text size="sm" fw={600}>{vuelo.CapM3}</Text></Box>
              <Box><Text size="xs" c="dimmed">Rem Kg</Text><Text size="sm" fw={600} c={(resumen?.capRemKg ?? 0) < 0 ? '#FA0073' : '#1b5e20'}>{resumen?.capRemKg ?? '—'}</Text></Box>
              <Box><Text size="xs" c="dimmed">Rem m³</Text><Text size="sm" fw={600} c={(resumen?.capRemM3 ?? 0) < 0 ? '#FA0073' : '#1b5e20'}>{resumen?.capRemM3 ?? '—'}</Text></Box>
              <Box><Text size="xs" c="dimmed">Total Kg</Text><Text size="sm" fw={600}>{resumen?.totalKg ?? '—'}</Text></Box>
              <Box><Text size="xs" c="dimmed">Total m³</Text><Text size="sm" fw={600}>{resumen?.totalM3 ?? '—'}</Text></Box>
            </Group>
          </Card>

          {/* FOC por día */}
          <Box mb="sm" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.65rem', border: '1px solid #e0e0e0', borderRadius: 6 }}>
              <thead>
                <tr style={{ backgroundColor: '#030D75' }}>
                  <th style={{ color: 'white', padding: '3px 6px', textAlign: 'left', fontSize: '0.6rem' }}>FOC Kg</th>
                  {WEEKDAYS_FULL.map((d) => <th key={d} style={{ color: 'white', padding: '3px 4px', textAlign: 'center', fontSize: '0.6rem', minWidth: 36 }}>{d}</th>)}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: '2px 6px', fontWeight: 600, fontSize: '0.6rem', borderRight: '1px solid #e0e0e0' }}>%</td>
                  {WEEKDAYS_FULL.map((d) => { const s = semaforo(focKg[d]); return <td key={d} style={{ backgroundColor: s.bg, color: s.c, fontWeight: 600, fontSize: '0.65rem', padding: '3px 4px', textAlign: 'center' }}>{focKg[d] || '0%'}</td>; })}
                </tr>
              </tbody>
            </table>
          </Box>

          <Divider mb="sm" />

          {/* Tabla de clientes */}
          <Text size="xs" fw={600} c="#020C41" mb="xs">Clientes asignados ({clientes.length})</Text>
          <Box style={{ overflowX: 'auto' }}>
            <Table withTableBorder withColumnBorders horizontalSpacing="sm" verticalSpacing="xs" style={{ fontSize: '0.75rem', textAlign: 'center' }}>
              <Table.Thead style={{ backgroundColor: '#030D75' }}>
                <Table.Tr>
                  <Table.Th style={{ color: 'white', textAlign: 'center', minWidth: 30 }}>P</Table.Th>
                  <Table.Th style={{ color: 'white', textAlign: 'left', minWidth: 150 }}>Cliente</Table.Th>
                  <Table.Th style={{ color: 'white', textAlign: 'center', minWidth: 60 }}>Kg</Table.Th>
                  <Table.Th style={{ color: 'white', textAlign: 'center', minWidth: 60 }}>m³</Table.Th>
                  {WEEKDAYS_FULL.map((d) => <Table.Th key={d} style={{ color: 'white', textAlign: 'center', minWidth: 38 }}>{d}</Table.Th>)}
                  <Table.Th style={{ color: 'white', textAlign: 'left', minWidth: 100 }}>Comentario</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {clientes.sort((a, b) => (a.prioridad || 0) - (b.prioridad || 0)).map((c) => (
                  <Table.Tr key={c.id}>
                    <Table.Td><Badge size="xs" color="#030D75" variant="filled" circle>{c.prioridad}</Badge></Table.Td>
                    <Table.Td style={{ textAlign: 'left', fontWeight: 600, color: '#020C41' }}>{c.nombre}</Table.Td>
                    <Table.Td style={{ fontWeight: 600 }}>{c.kg}</Table.Td>
                    <Table.Td style={{ fontWeight: 600 }}>{c.m3}</Table.Td>
                    {WEEKDAYS_FULL.map((d) => (
                      <Table.Td key={d} style={{ backgroundColor: c.dias?.[d] ? 'rgba(46,125,50,0.08)' : 'rgba(250,0,115,0.04)' }}>
                        <Text size="xs" fw={600} c={c.dias?.[d] ? 'green' : '#FA0073'}>{c.dias?.[d] ? '✓' : '✗'}</Text>
                      </Table.Td>
                    ))}
                    <Table.Td style={{ textAlign: 'left' }}><Text size="xs" c="dimmed" fs="italic" lineClamp={1}>{c.comentario || '—'}</Text></Table.Td>
                  </Table.Tr>
                ))}
                {!clientes.length && <Table.Tr><Table.Td colSpan={4 + WEEKDAYS_FULL.length + 1}><Text size="sm" c="dimmed" ta="center">Sin clientes</Text></Table.Td></Table.Tr>}
              </Table.Tbody>
            </Table>
          </Box>
        </Paper>
      )}

      {/* SIN SELECCIÓN */}
      {!loading && !vuelo && rutaFiltro && vueloFiltro && (
        <Paper shadow="xs" p="xl" radius="md" mb="md"><Text ta="center" c="dimmed">No se encontró información para este vuelo.</Text></Paper>
      )}
      {!loading && !vuelo && (!rutaFiltro || !vueloFiltro) && (
        <Paper shadow="xs" p="xl" radius="md" mb="md"><Text ta="center" c="dimmed">{!rutaFiltro ? 'Selecciona una ruta.' : 'Selecciona un vuelo.'}</Text></Paper>
      )}

      {/* RESUMEN GENERAL DE VUELOS */}
      <Paper shadow="xs" radius="md" style={{ overflow: 'hidden' }}>
        <Box p="sm" style={{ backgroundColor: '#030D75' }}>
          <Text size="sm" fw={600} c="white">Resumen de vuelos — {getMonthName(mes).toUpperCase()} {anio}</Text>
        </Box>
        <Box style={{ overflowX: 'auto' }}>
          <Table striped highlightOnHover withTableBorder horizontalSpacing="sm" verticalSpacing="xs" style={{ textAlign: 'center' }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ textAlign: 'center' }}>Ruta</Table.Th>
                <Table.Th style={{ textAlign: 'center' }}>Vuelo</Table.Th>
                <Table.Th style={{ textAlign: 'center' }}>Kg Asignados</Table.Th>
                <Table.Th style={{ textAlign: 'center' }}>m³ Asignados</Table.Th>
                <Table.Th style={{ textAlign: 'center' }}>Clientes</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {resumenVuelos.length === 0 ? (
                <Table.Tr><Table.Td colSpan={5}><Text size="sm" c="dimmed" ta="center">Sin datos</Text></Table.Td></Table.Tr>
              ) : (
                resumenVuelos.map((r, i) => (
                  <Table.Tr key={i} style={{ cursor: 'pointer' }} onClick={() => { setRutaFiltro(r.Ruta); setVueloFiltro(String(r.Vuelo)); }}>
                    <Table.Td><Badge variant="light" color="am-blue" size="sm">{r.Ruta}</Badge></Table.Td>
                    <Table.Td><Text size="sm" fw={600}>{r.Vuelo}</Text></Table.Td>
                    <Table.Td><Text size="sm" fw={600}>{Number(r.KgAsignados || 0).toLocaleString('es-MX')}</Text></Table.Td>
                    <Table.Td><Text size="sm" fw={600}>{Number(r.M3Asignados || 0).toLocaleString('es-MX', { maximumFractionDigits: 1 })}</Text></Table.Td>
                    <Table.Td><Badge size="sm" color="gray" variant="light">{r.Clientes || 0}</Badge></Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Box>
      </Paper>
    </>
  );
}
